// src/pages/PersonalPhotos.tsx
import React, { useState, useMemo } from 'react';
import { Box, Typography, Tabs, Tab, Paper } from '@mui/material';
import { Share as ShareIcon, Inbox as InboxIcon, Analytics as AnalyticsIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import SharedCollections from './SharedCollections';
import ReceivedCollections from './ReceivedCollections';
import DownloadAnalytics from './DownloadAnalytics';

interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

const TabPanel = React.memo(function TabPanel(props: TabPanelProps) {
   const { children, value, index, ...other } = props;

   return (
      <div
         role='tabpanel'
         hidden={value !== index}
         id={`personal-photos-tabpanel-${index}`}
         aria-labelledby={`personal-photos-tab-${index}`}
         {...other}
      >
         {value === index && <Box>{children}</Box>}
      </div>
   );
});

function a11yProps(index: number) {
   return {
      id: `personal-photos-tab-${index}`,
      'aria-controls': `personal-photos-tabpanel-${index}`,
   };
}

const PersonalPhotos: React.FC = () => {
   const location = useLocation();
   const navigate = useNavigate();

   // Determine initial tab based on current path
   const getInitialTab = useMemo(() => {
      if (location.pathname === '/received-collections') return 1;
      if (location.pathname === '/download-analytics') return 2;
      if (location.pathname === '/shared-collections') return 0;
      const urlParams = new URLSearchParams(location.search);
      const tab = urlParams.get('tab');
      if (tab === 'received') return 1;
      if (tab === 'analytics' || tab === 'downloads' || tab === 'downloaded') return 2;
      return 0;
   }, [location.pathname, location.search]);

   const [value, setValue] = useState(getInitialTab);

   const handleChange = React.useCallback(
      (event: React.SyntheticEvent, newValue: number) => {
         setValue(newValue);
         // Update URL based on tab selection
         if (newValue === 0) {
            navigate('/shared-collections', { replace: true });
         } else if (newValue === 1) {
            navigate('/received-collections', { replace: true });
         } else if (newValue === 2) {
            navigate('/download-analytics', { replace: true });
         }
      },
      [navigate]
   );

   // Update tab when URL changes
   React.useEffect(() => {
      if (location.pathname === '/received-collections') {
         setValue(1);
      } else if (location.pathname === '/download-analytics') {
         setValue(2);
      } else if (location.pathname === '/shared-collections') {
         setValue(0);
      } else if (location.pathname === '/personal-photos') {
         // Check query params for /personal-photos
         const urlParams = new URLSearchParams(location.search);
         const tab = urlParams.get('tab');
         if (tab === 'received') {
            setValue(1);
         } else if (tab === 'analytics' || tab === 'downloads' || tab === 'downloaded') {
            setValue(2);
         } else {
            setValue(0);
         }
      } else {
         const urlParams = new URLSearchParams(location.search);
         const tab = urlParams.get('tab');
         if (tab === 'received') {
            setValue(1);
         } else if (tab === 'analytics' || tab === 'downloads' || tab === 'downloaded') {
            setValue(2);
         } else {
            setValue(0);
         }
      }
   }, [location]);

   return (
      <Box sx={{ width: '100%' }}>
         <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Box sx={{ px: 3, pt: 3, pb: 0 }}>
               <Typography variant='h4' gutterBottom>
                  📸 My Photos
               </Typography>
               <Typography variant='body1' color='textSecondary' paragraph>
                  Manage your shared photo collections and view collections shared with you.
               </Typography>
            </Box>
            <Tabs
               value={value}
               onChange={handleChange}
               aria-label='personal photos tabs'
               variant="scrollable"
               scrollButtons="auto"
               allowScrollButtonsMobile
               sx={{ 
                  px: 3,
                  '& .MuiTabs-scrollButtons': {
                     '&.Mui-disabled': {
                        opacity: 0.3
                     }
                  }
               }}
            >
               <Tab
                  label='Shared by Me'
                  icon={<ShareIcon />}
                  iconPosition='start'
                  {...a11yProps(0)}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
               />
               <Tab
                  label='Shared with Me'
                  icon={<InboxIcon />}
                  iconPosition='start'
                  {...a11yProps(1)}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
               />
               <Tab
                  label='Downloads'
                  icon={<AnalyticsIcon />}
                  iconPosition='start'
                  {...a11yProps(2)}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
               />
            </Tabs>
         </Paper>

         <TabPanel value={value} index={0}>
            <SharedCollections />
         </TabPanel>
         <TabPanel value={value} index={1}>
            <ReceivedCollections />
         </TabPanel>
         <TabPanel value={value} index={2}>
            <DownloadAnalytics />
         </TabPanel>
      </Box>
   );
};

export default PersonalPhotos;
